import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Volume2, VolumeX, ArrowUpRight } from "lucide-react";
import Logo from "@/components/layout/Logo";

/**
 * ------------------------------------------------------------------
 * COCOON HERO VIDEO CONFIG
 * ------------------------------------------------------------------
 * 1. Upload your video to YouTube as UNLISTED (Studio → Upload → Visibility → Unlisted).
 * 2. Copy the video ID from the URL:
 *      https://youtu.be/XXXXXXXXXXX   →  XXXXXXXXXXX
 *      https://www.youtube.com/watch?v=XXXXXXXXXXX  →  XXXXXXXXXXX
 * 3. Paste it below and refresh. That's it.
 * ------------------------------------------------------------------
 */
const HERO_YOUTUBE_ID = "GRh8YyIBPvA";  // ← paste your YouTube video ID here

export default function Landing() {
  const nav = useNavigate();
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    if (!iframeRef.current) return;
    const func = muted ? "unMute" : "mute";
    iframeRef.current.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func, args: [] }),
      "*"
    );
    setMuted(!muted);
  };

  const ytSrc = HERO_YOUTUBE_ID
    ? `https://www.youtube-nocookie.com/embed/${HERO_YOUTUBE_ID}` +
      `?autoplay=1&mute=1&loop=1&playlist=${HERO_YOUTUBE_ID}` +
      `&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1` +
      `&iv_load_policy=3&disablekb=1&fs=0&enablejsapi=1`
    : null;

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-black" data-testid="landing-hero">
      {/* --- Background layer: video only (black backdrop while it loads) --- */}
      <div className="absolute inset-0 overflow-hidden bg-black">
        {ytSrc && (
          <iframe
            ref={iframeRef}
            src={ytSrc}
            title="Cocoon"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{
              // Landscape 16:9 video that always covers viewport (crops sides on portrait)
              width: "max(100vw, calc(100vh * 16 / 9))",
              height: "max(100vh, calc(100vw * 9 / 16))",
            }}
            data-testid="hero-video"
          />
        )}

        {/* Soft gradient veils so overlaid buttons + text remain legible */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-black/60 pointer-events-none" />
      </div>

      {/* --- Top bar: Sign in + Create account (right) --- */}
      <header className="relative z-10 flex items-center justify-end px-5 sm:px-8 lg:px-12 pt-5 sm:pt-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => nav("/portal/login")}
            className="rounded-full px-4 sm:px-5 py-2 sm:py-2.5 text-[10px] sm:text-[11px] tracking-[0.14em] uppercase text-white/95 backdrop-blur-xl bg-white/10 border border-white/25 hover:bg-white/20 transition-colors duration-300"
            data-testid="hero-signin-btn"
          >
            Sign in
          </button>
          <button
            onClick={() => nav("/portal/register")}
            className="rounded-full px-4 sm:px-5 py-2 sm:py-2.5 text-[10px] sm:text-[11px] tracking-[0.14em] uppercase text-black bg-white/95 hover:bg-white transition-colors duration-300 inline-flex items-center gap-1.5 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.35)]"
            data-testid="hero-register-btn"
          >
            Create account
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* --- Centered brand + tagline --- */}
      <section className="relative z-10 flex flex-col items-center justify-center text-center px-6 min-h-[calc(100vh-88px)]">
        <div className="mb-6 opacity-95">
          <Logo variant="mark" to={null} size={64} />
        </div>
        <p className="tracking-[0.28em] text-[10px] sm:text-[11px] text-white/75 mb-4 uppercase">
          Cocoon · Bali · Est. 2012
        </p>
        <h1
          className="font-editorial text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] max-w-3xl"
          data-testid="hero-headline"
        >
          Book your ritual,<br className="hidden sm:block" /> in a quiet place.
        </h1>
        <p className="text-sm sm:text-base text-white/80 max-w-md mt-6">
          Four sanctuaries across Bali. One membership.
        </p>
      </section>

      {/* --- Mute / unmute toggle --- */}
      {ytSrc && (
        <button
          onClick={toggleMute}
          className="absolute bottom-5 right-5 sm:bottom-8 sm:right-8 z-10 rounded-full p-3 text-white backdrop-blur-xl bg-white/10 border border-white/25 hover:bg-white/20 transition-colors duration-300"
          aria-label={muted ? "Unmute video" : "Mute video"}
          data-testid="video-mute-toggle"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      )}

      {/* --- Bottom-left tiny footer --- */}
      <p
        className="absolute bottom-5 left-5 sm:bottom-8 sm:left-8 z-10 text-[10px] tracking-[0.14em] uppercase text-white/60"
        data-testid="hero-footer"
      >
        © 2026 Cocoon Medical Spa
      </p>

      {/* --- Center-bottom Staff Login text link --- */}
      <button
        onClick={() => nav("/staff/login")}
        className="absolute bottom-5 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 text-[9px] sm:text-[10px] tracking-[0.14em] uppercase text-white/80 hover:text-white transition-colors duration-300 bg-transparent border-0 p-0 cursor-pointer"
        data-testid="staff-portal-link"
      >
        Staff Login
      </button>
    </div>
  );
}
